import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../utils/supabase';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { useDataCache } from '../context/DataCacheContext';
import { CACHE_KEYS } from '../constants/cacheKeys';

export const useDefaultFocus = () => {
  const { user } = useAuth();
  const [defaultFocusId, setDefaultFocusId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  const fetchDefaultFocus = useCallback(async () => {
    if (!user?.id) {
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('user_career_focuses')
        .select('id')
        .eq('user_id', user.id)
        .eq('is_active', true)
        .eq('is_default', true)
        .maybeSingle();

      if (error) {
        console.error('[useDefaultFocus] Query error:', error);
      } else {
        setDefaultFocusId(data?.id || null);
      }
    } catch (err) {
      console.error('[useDefaultFocus] Error:', err);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchDefaultFocus();
  }, [fetchDefaultFocus, refreshKey]);

  const refresh = useCallback(() => {
    setRefreshKey(prev => prev + 1);
  }, []);

  return { defaultFocusId, loading, refresh };
};

export const useSetDefaultFocus = () => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const { invalidateCache } = useDataCache();
  const [isSetting, setIsSetting] = useState(false);

  const setDefaultFocus = async (focusId: string | null): Promise<boolean> => {
    if (!user?.id) {
      showToast('You must be logged in', 'error');
      return false;
    }

    setIsSetting(true);
    try {
      // First, remove any existing default
      const { error: removeError } = await supabase
        .from('user_career_focuses')
        .update({ is_default: false })
        .eq('user_id', user.id)
        .eq('is_default', true);

      if (removeError && removeError.code !== 'PGRST116') {
        throw removeError;
      }

      // Then set new default if provided
      if (focusId) {
        const { error: setError } = await supabase
          .from('user_career_focuses')
          .update({ is_default: true })
          .eq('id', focusId)
          .eq('user_id', user.id)
          .eq('is_active', true);

        if (setError) throw setError;

        showToast('Default focus updated', 'success');
      } else {
        showToast('Default focus cleared', 'success');
      }

      // Don't invalidate focus caches - only the is_default flag changed, not the focus data itself

      return true;
    } catch (error) {
      console.error('Failed to set default focus:', error);
      showToast('Failed to update default focus', 'error');
      return false;
    } finally {
      setIsSetting(false);
    }
  };

  return { setDefaultFocus, isSetting };
};